import React, { useState } from 'react';
import {
    BLOCK_LABELS,
    BLOCKS_BY_KIND,
    SECTION_BACKGROUNDS,
    ToolContentDocument,
    ToolContentKind,
    ToolDetailBlock,
    ToolDetailBlockType,
} from '@easytools/tool-content';
import { BlockAt, DRAG_MOVE, DRAG_NEW, Target, moveBlock, parseMove } from './documentOps';
import { SectionTemplate, templatesFor } from './sectionTemplates';

/** Icona Bootstrap e riga di spiegazione per ogni tipo di blocco, nella libreria. */
const BLOCK_INFO: Record<ToolDetailBlockType, { icon: string; hint: string }> = {
    heading: { icon: 'bi-type-h2', hint: 'Titolo con riga sopra e sottotitolo' },
    button: { icon: 'bi-hand-index', hint: 'Un pulsante verso una pagina' },
    mediaText: { icon: 'bi-layout-text-sidebar-reverse', hint: 'Testo accanto a un’immagine' },
    quote: { icon: 'bi-quote', hint: 'Citazione o testimonianza' },
    stats: { icon: 'bi-123', hint: 'Numeri in evidenza' },
    cards: { icon: 'bi-postcard', hint: 'Schede con icona, testo e link' },
    video: { icon: 'bi-play-btn', hint: 'Video di YouTube o Vimeo' },
    divider: { icon: 'bi-dash-lg', hint: 'Riga o spazio tra blocchi' },
    logos: { icon: 'bi-buildings', hint: 'Loghi dei clienti' },
    hero: { icon: 'bi-type-h1', hint: 'Frase di apertura e punti chiave' },
    richText: { icon: 'bi-text-paragraph', hint: 'Paragrafi, titoli, elenchi' },
    featureGrid: { icon: 'bi-grid-3x2-gap', hint: 'Funzioni con icona' },
    callout: { icon: 'bi-info-square', hint: 'Avviso o nota in evidenza' },
    steps: { icon: 'bi-list-ol', hint: 'Procedura numerata' },
    chipList: { icon: 'bi-tags', hint: 'Elenco breve a pillole' },
    specs: { icon: 'bi-table', hint: 'Coppie etichetta e valore' },
    faq: { icon: 'bi-question-circle', hint: 'Domande frequenti' },
    media: { icon: 'bi-image', hint: 'Un\'immagine con didascalia' },
    gallery: { icon: 'bi-images', hint: 'Più schermate affiancate' },
    cta: { icon: 'bi-box-arrow-in-right', hint: 'Invito ad accedere' },
    related: { icon: 'bi-diagram-3', hint: 'Altri tool della categoria' },
    section: { icon: 'bi-bookmark', hint: 'Titolo di capitolo' },
    table: { icon: 'bi-table', hint: 'Tabella a più colonne' },
    pageBreak: { icon: 'bi-file-break', hint: 'Nuova pagina nel PDF' },
};

/** Nome breve di un blocco nella struttura: il suo primo testo, se c'è. */
function blockName(block: ToolDetailBlock): string {
    const first = (value: unknown): string | undefined => {
        if (typeof value === 'string') return value.trim() || undefined;
        if (Array.isArray(value)) for (const item of value) { const found = first(item); if (found) return found; }
        if (value && typeof value === 'object') {
            for (const [key, item] of Object.entries(value)) {
                if (key === 'type' || key === 'icon' || key === 'variant' || key === 'url') continue;
                const found = first(item);
                if (found) return found;
            }
        }
        return undefined;
    };
    return (first(block) ?? '').replace(/[*`#_]/g, '').slice(0, 60);
}

interface Props {
    kind: ToolContentKind;
    doc: ToolContentDocument;
    target: Target | null;
    onSelect: (target: Target) => void;
    onChange: (doc: ToolContentDocument, select?: Target) => void;
    /** Aggiunge un blocco dove indica la selezione (dopo il blocco, in fondo alla sezione o alla pagina). */
    onAdd: (type: ToolDetailBlockType) => void;
    /** Aggiunge una sezione pronta dopo quella selezionata, o in fondo. */
    onAddSection: (template: SectionTemplate) => void;
}

/**
 * Colonna sinistra dell'editor: la struttura della pagina (sezioni e blocchi, da
 * selezionare e riordinare trascinando) e la libreria dei blocchi da aggiungere.
 */
const ContentOutline: React.FC<Props> = ({ kind, doc, target, onSelect, onChange, onAdd, onAddSection }) => {
    const [tab, setTab] = useState<'blocks' | 'sections'>('blocks');
    const [dropAt, setDropAt] = useState<string | null>(null);

    const dropOn = (e: React.DragEvent, at: BlockAt) => {
        e.preventDefault();
        setDropAt(null);
        const moved = parseMove(e.dataTransfer.getData(DRAG_MOVE));
        if (!moved) return;
        const [next, landed] = moveBlock(doc, moved, at);
        onChange(next, { kind: 'block', ...landed });
    };
    const over = (e: React.DragEvent, key: string) => {
        if (!e.dataTransfer.types.includes(DRAG_MOVE)) return;
        e.preventDefault();
        if (dropAt !== key) setDropAt(key);
    };

    return (
        <div className="tcs-outline">
            <section className="tcs-outline__group" aria-labelledby="tcs-outline-structure">
                <h3 id="tcs-outline-structure" className="tcs-outline__title">Struttura</h3>
                <button type="button" className={`tcs-outline__page${target?.kind === 'page' ? ' is-on' : ''}`} onClick={() => onSelect({ kind: 'page' })}>
                    <i className="bi bi-file-earmark-text" aria-hidden="true" /> Impostazioni della pagina
                </button>
                {doc.sections.length === 0 && <p className="tcs-outline__empty">Ancora nessun blocco: scegline uno qui sotto.</p>}
                <ol className="tcs-outline__tree">
                    {doc.sections.map((section, s) => (
                        <li key={s}>
                            <button type="button"
                                className={`tcs-outline__section${target?.kind === 'section' && target.section === s ? ' is-on' : ''}${dropAt === `s${s}` ? ' is-drop' : ''}`}
                                onClick={() => onSelect({ kind: 'section', section: s })}
                                onDragOver={e => over(e, `s${s}`)} onDragLeave={() => setDropAt(null)}
                                onDrop={e => dropOn(e, { section: s, index: section.blocks.length })}>
                                <span className={`tcs-outline__swatch tcs-outline__swatch--${section.background ?? 'plain'}`} aria-hidden="true" />
                                Sezione {s + 1}
                                <small>{SECTION_BACKGROUNDS.find(b => b.value === (section.background ?? 'plain'))?.label}{section.layout === 'split' ? ' · 2 colonne' : ''}</small>
                            </button>
                            <ol>
                                {section.blocks.map((block, index) => {
                                    const on = target?.kind === 'block' && target.section === s && target.index === index;
                                    const key = `b${s}-${index}`;
                                    return (
                                        <li key={index}>
                                            <button type="button" draggable
                                                className={`tcs-outline__block${on ? ' is-on' : ''}${dropAt === key ? ' is-drop' : ''}`}
                                                onClick={() => onSelect({ kind: 'block', section: s, index })}
                                                onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData(DRAG_MOVE, `${s}:${index}`); }}
                                                onDragOver={e => over(e, key)} onDragLeave={() => setDropAt(null)}
                                                onDrop={e => dropOn(e, { section: s, index })}>
                                                <i className={`bi ${BLOCK_INFO[block.type].icon}`} aria-hidden="true" />
                                                <span>
                                                    <strong>{BLOCK_LABELS[block.type]}</strong>
                                                    {blockName(block) && <small>{blockName(block)}</small>}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ol>
                        </li>
                    ))}
                </ol>
            </section>

            <section className="tcs-outline__group" aria-label="Aggiungi">
                <div className="tcs-choice__options tcs-outline__tabs" role="tablist" aria-label="Cosa aggiungere">
                    <button type="button" role="tab" aria-selected={tab === 'blocks'} className={`tcs-choice__option${tab === 'blocks' ? ' is-on' : ''}`} onClick={() => setTab('blocks')}>Blocchi</button>
                    <button type="button" role="tab" aria-selected={tab === 'sections'} className={`tcs-choice__option${tab === 'sections' ? ' is-on' : ''}`} onClick={() => setTab('sections')}>Sezioni pronte</button>
                </div>
                {tab === 'blocks' ? (
                    <>
                        <p className="tcs-outline__hint">
                            Clic per aggiungerlo {target?.kind === 'block' ? 'dopo il blocco selezionato' : target?.kind === 'section' ? 'in fondo alla sezione selezionata' : 'in fondo alla pagina'}, oppure trascinalo nel punto che vuoi.
                        </p>
                        <div className="tcs-library" role="tabpanel">
                            {BLOCKS_BY_KIND[kind].map(type => (
                                <button key={type} type="button" className="tcs-library__item" draggable title={BLOCK_INFO[type].hint}
                                    onDragStart={e => { e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData(DRAG_NEW, type); }}
                                    onClick={() => onAdd(type)}>
                                    <i className={`bi ${BLOCK_INFO[type].icon}`} aria-hidden="true" />
                                    <span>{BLOCK_LABELS[type]}</span>
                                </button>
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <p className="tcs-outline__hint">
                            Struttura e stile già pronti, testi da scrivere. Si aggiunge {target?.kind === 'page' || !target ? 'in fondo alla pagina' : 'dopo la sezione selezionata'}.
                        </p>
                        <ul className="tcs-templates" role="tabpanel">
                            {templatesFor(kind).map(template => (
                                <li key={template.id}>
                                    <button type="button" className="tcs-templates__item" onClick={() => onAddSection(template)}>
                                        <i className={`bi ${template.icon}`} aria-hidden="true" />
                                        <span><strong>{template.name}</strong><small>{template.hint}</small></span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </section>
        </div>
    );
};

export default ContentOutline;
